import { useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import { format, isValid, parseISO, set } from "date-fns";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";
import * as Popover from "@radix-ui/react-popover";
import { Calendar as CalendarIcon, Clock, X } from "lucide-react";
import "react-day-picker/style.css";
import type { Json } from "../core";
import { useLocale } from "../react/providers/LocaleProvider";

// ─── Date / DateTime pickers ─────────────────────────────────────────────────
// Calendar surface is react-day-picker (single mode) rendered inside a Radix
// Popover — the same popover chrome the reference / m2m comboboxes use, so the
// trigger and surface match the rest of the form. Radix has no calendar
// primitive, so react-day-picker fills that gap; its layout ships via the
// imported `style.css` and we remap its `--rdp-*` variables to our theme
// tokens in `_date-picker.scss`.
//
// Wire / timezone contract:
//   date     — a calendar date has no timezone. Stored + displayed verbatim
//              as "yyyy-MM-dd"; no tz conversion (matches the read-view
//              `fmtDate`, which also formats naively).
//   datetime — stored / sent to the API in UTC (ISO-8601 with a `Z`), but
//              SHOWN and EDITED in the user's locale timezone — exactly what
//              the read-view `fmtDateTime(value, locale.timezone)` does. The
//              picker converts UTC → tz for display (`toZonedTime`) and tz →
//              UTC on save (`fromZonedTime`), so read and edit always agree.

const DATE_WIRE = "yyyy-MM-dd";
const DATETIME_UTC_WIRE = "yyyy-MM-dd'T'HH:mm:ss'Z'";

// Far-but-finite bounds so the month/year dropdown caption has a range to
// render without reaching for the current date at module load.
const RANGE_START = new Date(1950, 0, 1);
const RANGE_END = new Date(2100, 11, 31);

function parseValue(value: unknown): Date | undefined {
    if (value == null || value === "") return undefined;
    const parsed = parseISO(String(value));
    return isValid(parsed) ? parsed : undefined;
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const MINUTES = Array.from({ length: 60 }, (_, m) => m);
const pad2 = (n: number): string => String(n).padStart(2, "0");

interface PickerProps {
    value: unknown;
    onChange: (next: string | null) => void;
    disabled?: boolean;
    placeholder?: string;
    /** Open the popover on mount — used by inline list-cell editing. */
    autoOpen?: boolean;
    /** Fired when the popover closes (commit/cancel decision lives in caller). */
    onClose?: () => void;
    /** Override the trigger button class (defaults to the shared select trigger). */
    triggerClassName?: string;
}

// Children rendered inside Radix's own Popover.Trigger button (no `asChild`,
// so the trigger stays a real focusable <button> and data-state is managed for
// us). The clear affordance is a non-button <span> — valid inside a <button> —
// that stops propagation so it never toggles the popover.
function triggerInner(
    label: string,
    placeholder: string | undefined,
    onClear: (() => void) | undefined,
) {
    return (
        <>
            <span className="date-picker__value">
                <CalendarIcon size={14} className="date-picker__lead-icon" />
                {label
                    ? <span>{label}</span>
                    : <span className="cell-muted">{placeholder ?? "Select…"}</span>}
            </span>
            {label && onClear ? (
                <span
                    role="button"
                    aria-label="Clear"
                    className="date-picker__clear"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                    }}
                >
                    <X size={13} />
                </span>
            ) : null}
        </>
    );
}

export function DateField({
    value,
    onChange,
    disabled,
    placeholder,
    autoOpen,
    onClose,
    triggerClassName = "rdx-select__trigger date-picker__trigger",
}: PickerProps) {
    const [open, setOpen] = useState(Boolean(autoOpen));
    const selected = useMemo(() => parseValue(value), [value]);
    const label = selected ? format(selected, "dd MMM yyyy") : "";

    function handleOpenChange(next: boolean) {
        if (disabled) return;
        setOpen(next);
        if (!next) onClose?.();
    }

    function handleSelect(day: Date | undefined) {
        onChange(day ? format(day, DATE_WIRE) : null);
        handleOpenChange(false);
    }

    return (
        <Popover.Root open={open} onOpenChange={handleOpenChange}>
            <Popover.Trigger className={triggerClassName} disabled={disabled}>
                {triggerInner(label, placeholder, disabled ? undefined : () => onChange(null))}
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content className="date-picker__popover" align="start" sideOffset={4} collisionPadding={12}>
                    <DayPicker
                        mode="single"
                        selected={selected}
                        onSelect={handleSelect}
                        defaultMonth={selected}
                        captionLayout="dropdown"
                        startMonth={RANGE_START}
                        endMonth={RANGE_END}
                        showOutsideDays
                        weekStartsOn={1}
                    />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

export function DateTimeField({
    value,
    onChange,
    disabled,
    placeholder,
    autoOpen,
    onClose,
    triggerClassName = "rdx-select__trigger date-picker__trigger",
}: PickerProps) {
    const { timezone } = useLocale();
    const tz = timezone || "UTC";
    const [open, setOpen] = useState(Boolean(autoOpen));

    // The stored value is a UTC instant. `zoned` is a Date whose LOCAL fields
    // (getHours / getFullYear / …) read as the wall-clock in `tz` — that's what
    // react-day-picker and the time selects render. The label formats the same
    // instant directly in `tz`, matching the read-view `fmtDateTime`.
    const instant = useMemo(() => parseValue(value), [value]);
    const zoned = useMemo(() => (instant ? toZonedTime(instant, tz) : undefined), [instant, tz]);
    const label = instant ? formatInTimeZone(instant, tz, "dd MMM yyyy, HH:mm") : "";
    const hours = zoned ? zoned.getHours() : 0;
    const minutes = zoned ? zoned.getMinutes() : 0;

    function handleOpenChange(next: boolean) {
        if (disabled) return;
        setOpen(next);
        if (!next) onClose?.();
    }

    // `wallClock` carries the chosen tz wall-clock in its local fields;
    // `fromZonedTime` reinterprets those fields as `tz` and returns the UTC
    // instant, which we emit as ISO-8601 with a `Z`.
    function emit(wallClock: Date) {
        onChange(formatInTimeZone(fromZonedTime(wallClock, tz), "UTC", DATETIME_UTC_WIRE));
    }

    function handleDay(day: Date | undefined) {
        if (!day) {
            onChange(null);
            return;
        }
        // `day` is react-day-picker's selection at browser-local midnight; its
        // Y/M/D are the chosen calendar day. Graft the current time onto it.
        emit(set(day, { hours, minutes, seconds: 0, milliseconds: 0 }));
    }

    // A bare time edit with no date yet anchors to "now in tz" so the user
    // isn't blocked from setting a time first.
    function handleTime(nextHours: number, nextMinutes: number) {
        const base = zoned ?? toZonedTime(new Date(), tz);
        emit(set(base, { hours: nextHours, minutes: nextMinutes, seconds: 0, milliseconds: 0 }));
    }

    return (
        <Popover.Root open={open} onOpenChange={handleOpenChange}>
            <Popover.Trigger className={triggerClassName} disabled={disabled}>
                {triggerInner(label, placeholder, disabled ? undefined : () => onChange(null))}
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content className="date-picker__popover" align="start" sideOffset={4} collisionPadding={12}>
                    <DayPicker
                        mode="single"
                        selected={zoned}
                        onSelect={handleDay}
                        defaultMonth={zoned}
                        captionLayout="dropdown"
                        startMonth={RANGE_START}
                        endMonth={RANGE_END}
                        showOutsideDays
                        weekStartsOn={1}
                    />
                    <div className="date-picker__time">
                        <Clock size={14} className="date-picker__lead-icon" />
                        <select
                            className="date-picker__time-select"
                            value={hours}
                            onChange={(e) => handleTime(Number(e.target.value), minutes)}
                        >
                            {HOURS.map((h) => (
                                <option key={h} value={h}>{pad2(h)}</option>
                            ))}
                        </select>
                        <span className="date-picker__time-sep">:</span>
                        <select
                            className="date-picker__time-select"
                            value={minutes}
                            onChange={(e) => handleTime(hours, Number(e.target.value))}
                        >
                            {MINUTES.map((m) => (
                                <option key={m} value={m}>{pad2(m)}</option>
                            ))}
                        </select>
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

// Inline list-cell editors — wrap the controlled pickers with the
// EditCellContext commit/cancel state machine. Date commits on pick; datetime
// stages a draft and commits when the popover closes (so the user can set both
// day and time before the cell exits edit mode).

export function DateCellEditor({
    value,
    commit,
    cancel,
}: {
    value: Json | undefined;
    commit: (next: Json | null) => void;
    cancel: () => void;
}) {
    const committed = useRef(false);
    return (
        <DateField
            value={value}
            autoOpen
            triggerClassName="cell-edit-input date-picker__trigger"
            placeholder="Select date"
            onChange={(next) => {
                committed.current = true;
                commit(next);
            }}
            onClose={() => {
                if (!committed.current) cancel();
            }}
        />
    );
}

export function DateTimeCellEditor({
    value,
    commit,
    cancel,
}: {
    value: Json | undefined;
    commit: (next: Json | null) => void;
    cancel: () => void;
}) {
    const original = value ?? null;
    const [draft, setDraft] = useState<Json | null>(value ?? null);
    return (
        <DateTimeField
            value={draft}
            autoOpen
            triggerClassName="cell-edit-input date-picker__trigger"
            placeholder="Select date & time"
            onChange={(next) => setDraft(next)}
            onClose={() => {
                if (draft !== original) commit(draft);
                else cancel();
            }}
        />
    );
}
